import React, { FunctionComponent, useState } from 'react'
import { injectIntl, InjectedIntlProps } from 'react-intl'
import { EventSection } from 'src/components/calendar/eventSection'
import { Box } from 'src/components/calendar/Box'
import { LoadingContent } from 'src/components/loading-content/LoadingContent'
import { LoadingPlaceholder } from 'src/components/loading-content/LoadingPlaceholder'
import { DATE_AND_TIME_ID, MAX_DATE_BOXES_TO_SHOW } from './constants'
import { formattedTimeOnlyRange, formattedMultipleDateRange } from 'src/lib/formatter'
import { guessZone } from 'src/lib/display-date'
import { trackGetTickets } from 'src/lib/analytics'

const style = require('./DateAndTime.css')
import locale from './locale'

interface Props {
  eventData: any,
  loading: Boolean,
  viewer: any,
}

declare var $u

const Placeholder = () => (
  <LoadingPlaceholder modifierClass={style.placeholder}>
    <LoadingContent width="170px" height="25px" borderRadius="rounder" />
    <LoadingContent width="138px" height="62px" borderRadius="sharper" />
  </LoadingPlaceholder>
)

const DateAndTimeComponent: FunctionComponent<Props & InjectedIntlProps> = ({
  eventData,
  loading,
  intl,
  viewer,
}) => {
  const { formatMessage } = intl
  const [selectedSlot, setSelectedSlot] = useState(-1)
  const [showAll, setShowAll] = useState(false)

  const renderBoxes = (slots) => slots.map((event, idx) => (renderBox(event, idx)))

  const renderBox = (event, idx) => {

    let startAt, endAt, tz = null

    if (eventData.virtual) {
      startAt = new Date(1000 * event.startStamp)
      endAt = new Date(1000 * event.endStamp)
      tz = guessZone(startAt)
    } else {
      startAt = event.startAt
      endAt = event.endAt
    }

    return (
      <Box
        key={idx}
        date={formattedMultipleDateRange({ startAt, endAt, intl, totalCount: 1 })}
        time={formattedTimeOnlyRange({ startAt, endAt, intl, tz })}
        isOnSale={isOnSale(event, eventData.allAvailableTimeSlots)}
        handleClick={() => selectDate(idx, eventData)}
        selected={isSlotSelected(idx)}
      />
    )
  }

  const isOnSale = (timeSlot, availableSlots) => (
    timeSlot.state !== 'FULL' &&
    availableSlots.some(slot => ((slot.startAt === timeSlot.startAt) && (slot.endAt === timeSlot.endAt)))
  )

  const selectDate = (idx, eventData) => {
    setSelectedSlot(idx)
    getJunoUrl(eventData, idx)
  }

  const isSlotSelected = (idx) => (idx === selectedSlot)

  const getJunoUrl = (eventData, idx) => {
    const eventId = eventData.id;
    const hostId = eventData.user.id;
    const timedEntry = eventData.timedEntry;
    $u.open(eventId, eventId ? { locale, currentEventId: eventData.allTimeSlots[idx].id } : null)
    const trackingParams = { timedEntry, eventId, hostId, widget: 'date_time_button', viewer }
    trackGetTickets(trackingParams)
  };

  const determineSlicedTimeSlots = () => {
    if (eventData && 
        eventData.allTimeSlots.length == 6 || eventData.allTimeSlots.length == 8) return eventData.allTimeSlots.slice(0, MAX_DATE_BOXES_TO_SHOW)
    return eventData.allTimeSlots.slice(0, MAX_DATE_BOXES_TO_SHOW - 1)
  }

  const getTimeSlots = () => (
    showAll
    ? eventData.allTimeSlots
    : determineSlicedTimeSlots()
    /* TODO: This is a quick fix for rendering exactly six or eight timeslots depending on screen width,
       we'll want to come back to this at some point and find a tidier solution for this issue. */
  )

  const showMoreLessBox = (eventData && eventData.timeSlots.totalCount > MAX_DATE_BOXES_TO_SHOW)

  if (loading) return <Placeholder />

  if (eventData.hiddenDate) return null

  return (
    <EventSection id={style.dateTime} sectionId={DATE_AND_TIME_ID} heading={formatMessage(locale.dateAndTime)}>
      <div className={style.container}>
        {eventData && renderBoxes(getTimeSlots())}
        {showMoreLessBox &&
          <div className={style.showMoreLessItems} onClick={() => setShowAll(!showAll)}>
            <div className={style.showMoreLessItemsText}>
              {showAll ? formatMessage(locale.lessDatesAndTimes) : formatMessage(locale.moreDatesAndTimes)}
            </div>
          </div>
        }
      </div>
    </EventSection>
  )
}

export const DateAndTime = injectIntl(DateAndTimeComponent)
